import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchFoodLog, fetchPartners, updateFoodLog } from "api";
import { PageHeader, FormField, Spinner, Select } from "components/common";
import { useAuth } from "hooks/useAuth";

interface FormState {
  partner_entity_id:      string;
  receiving_partner_id:   string;
  poundage:               string;
  description:            string;
  estimated_retail_value: string;
  completed_at:           string;
}

export default function EditLogForm() {
  const { id }      = useParams<{ id: string }>();
  const navigate    = useNavigate();
  const queryClient = useQueryClient();
  const { isAdmin, isCourier } = useAuth();
  const logId = Number(id);

  const [form, setForm] = useState<FormState | null>(null);

  const { data: log, isLoading: logLoading } = useQuery({
    queryKey: ["foodLogs", logId],
    queryFn:  () => fetchFoodLog(logId),
  });

  const { data: partners = [] } = useQuery({
    queryKey: ["partners"],
    queryFn:  fetchPartners,
  });

  const donators     = partners.filter((p) => p.type === "donator" && p.active);
  const distributors = partners.filter((p) => p.type === "distributor" && p.active);

  // Pre-populate form once log is loaded
  useEffect(() => {
    if (log && !form) {
      setForm({
        partner_entity_id:      String(log.partner_entity_id),
        receiving_partner_id:   log.receiving_partner_id != null ? String(log.receiving_partner_id) : "",
        poundage:               String(log.poundage),
        description:            log.description ?? "",
        estimated_retail_value: log.estimated_retail_value != null ? String(log.estimated_retail_value) : "",
        completed_at:           log.completed_at
          ? log.completed_at.slice(0, 16) // trim to datetime-local format
          : "",
      });
    }
  }, [log, form]);

  const set = (field: keyof FormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm((prev) => prev ? { ...prev, [field]: e.target.value } : prev);

  const { mutate, isPending, error } = useMutation({
    mutationFn: () => updateFoodLog(logId, {
      partner_entity_id:      Number(form!.partner_entity_id),
      receiving_partner_id:   form!.receiving_partner_id ? Number(form!.receiving_partner_id) : undefined,
      poundage:               Number(form!.poundage),
      description:            form!.description || undefined,
      estimated_retail_value: form!.estimated_retail_value ? Number(form!.estimated_retail_value) : undefined,
      completed_at:           form!.completed_at || undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["foodLogs"] });
      navigate(`/logs/${logId}`);
    },
  });

  if (logLoading || !form) return <Spinner className="mt-20" />;

  const isValid = form.poundage && Number(form.poundage) > 0 && !!form.partner_entity_id;

  return (
    <div className="px-4 py-6 sm:px-8 sm:py-8 max-w-lg">
      <button
        onClick={() => navigate(-1)}
        className="text-sm text-gray-400 hover:text-gray-600 mb-6 transition-colors"
      >
        ← Back
      </button>

      <PageHeader title="Edit log" subtitle={`Entry #${logId}`} />

      <form
        onSubmit={(e) => { e.preventDefault(); if (isValid) mutate(); }}
        className="card p-6 space-y-5"
      >
        {/* Donating partner — admin can reassign, courier sees read-only */}
        {isAdmin ? (
          <FormField label="Donating partner">
            <Select value={form.partner_entity_id} onChange={set("partner_entity_id")}>
              <option value="" disabled>Select donating partner…</option>
              {donators.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </Select>
          </FormField>
        ) : (
          <FormField label="Donating partner">
            <Select value={form.partner_entity_id} disabled>
              {donators.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </Select>
          </FormField>
        )}

        {/* Receiving partner — admin/courier */}
        {(isAdmin || isCourier) && (
          <FormField label="Received by">
            <Select value={form.receiving_partner_id} onChange={set("receiving_partner_id")}>
              <option value="">None (optional)</option>
              {distributors.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </Select>
            <p className="text-xs text-gray-400 mt-1">
              The downstream partner receiving this food.
            </p>
          </FormField>
        )}

        <FormField label="Poundage (lbs)">
          <input
            className="input"
            type="number"
            min={1}
            step={1}
            value={form.poundage}
            onChange={set("poundage")}
            required
          />
        </FormField>

        <FormField label="Estimated retail value ($)">
          <input
            className="input"
            type="number"
            min={0}
            step={0.01}
            value={form.estimated_retail_value}
            onChange={set("estimated_retail_value")}
            placeholder="Optional"
          />
        </FormField>

        <FormField label="Completed at">
          <input
            className="input"
            type="datetime-local"
            value={form.completed_at}
            onChange={set("completed_at")}
          />
        </FormField>

        <FormField label="Description">
          <textarea
            className="input min-h-24 resize-y"
            value={form.description}
            onChange={set("description")}
            placeholder="Optional notes about this pickup…"
          />
        </FormField>

        {error && (
          <p className="text-sm text-red-500">Something went wrong. Please try again.</p>
        )}

        <div className="flex gap-3 pt-2">
          <button type="button" className="btn-secondary" onClick={() => navigate(-1)}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={!isValid || isPending}>
            {isPending ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}
